import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

function Profile() {
  const { currentUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [userData, setUserData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({
      ...userData,
      [name]: value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    // Validate inputs
    if (!userData.name || !userData.email) {
      setError('Name and email are required');
      return;
    }
    
    // If changing password, validate password fields
    if (userData.newPassword) {
      if (!userData.currentPassword) {
        setError('Current password is required to set a new password');
        return;
      }
      
      if (userData.newPassword.length < 6) {
        setError('New password must be at least 6 characters long');
        return;
      }
      
      if (userData.newPassword !== userData.confirmNewPassword) {
        setError('New passwords do not match');
        return;
      }
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Prepare update data
      const updateData = {
        name: userData.name,
        email: userData.email
      };
      
      // Add password change if requested
      if (userData.newPassword) {
        updateData.currentPassword = userData.currentPassword;
        updateData.newPassword = userData.newPassword;
      }
      
      // Call API to update profile
      const response = await apiService.users.updateProfile(updateData);
      
      setSuccessMessage('Profile updated successfully');
      console.log('Profile updated:', response);
      
      // Clear password fields
      setUserData({
        ...userData,
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });
      
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
      // Redirect will be handled by the App component
    } catch (err) {
      console.error('Logout failed:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="alert alert-warning">
        You need to be logged in to view this page.
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="card">
        <div className="card-header">
          <h2>Your Profile</h2>
        </div>
        <div className="card-body">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          
          {successMessage && (
            <div className="alert alert-success" role="alert">
              {successMessage}
            </div>
          )}
          
          <form onSubmit={handleUpdateProfile}>
            <div className="mb-3">
              <label htmlFor="name" className="form-label">Full Name</label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                value={userData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email address</label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={userData.email}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
            
            <h4 className="mt-4">Change Password</h4>
            <div className="mb-3">
              <label htmlFor="currentPassword" className="form-label">Current Password</label>
              <input
                type="password"
                className="form-control"
                id="currentPassword"
                name="currentPassword"
                value={userData.currentPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="newPassword" className="form-label">New Password</label>
              <input
                type="password"
                className="form-control"
                id="newPassword"
                name="newPassword"
                value={userData.newPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
              <div className="form-text">Leave blank to keep your current password.</div>
            </div>
            
            <div className="mb-3">
              <label htmlFor="confirmNewPassword" className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-control"
                id="confirmNewPassword"
                name="confirmNewPassword"
                value={userData.confirmNewPassword}
                onChange={handleInputChange}
                disabled={loading}
              />
            </div>
            
            <div className="d-flex justify-content-between">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Updating...' : 'Update Profile'}
              </button>
              
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleLogout}
                disabled={loading}
              >
                {loading ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Profile; 